// Camada ÚNICA de telemetria de produto (GA4 via gtag.js + Microsoft Clarity +
// PostHog).
// As tags globais são carregadas no index.html; aqui só damos um contrato TIPADO e
// resiliente por cima de `window.gtag` — todo evento do app passa por `track()`,
// então o nome e a forma dos parâmetros ficam consistentes em todos os lugares.
//
// Regra de ouro (LGPD + política do GA4): NADA de dado pessoal do lead como
// parâmetro (nome, telefone, e-mail, CPF). Só IDs internos, categorias e números.
// Se o gtag não existir (ad-block, ambiente de teste, SSR), `track` vira no-op.

import { phCapture, phIdentify, phPageView, phReset } from "./posthog.js";

export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "";

// Origem de uma ação de discagem — de qual tela o vendedor disparou a ligação.
export type DialerOrigin = "softphone" | "dialer" | "campaigns" | "ai_test";

// Mapa tipado dos eventos custom do produto → parâmetros esperados. Adicionar um
// evento novo aqui força o mesmo nome/mesma forma em todo disparo (sem digitar
// "click_to_call" à mão em cada arquivo e correr o risco de divergir).
export interface AnalyticsEventMap {
  // ── Discador / telefonia ────────────────────────────────────────────────
  /** Vendedor iniciou uma ligação (o clique que dispara a discagem). */
  click_to_call: { origin: DialerOrigin };
  /** Ação no discador — cruzar com os dados do PABX para medir sinergia. */
  dialer_action: { action: "call" | "hangup" | "mute" | "unmute" | "dtmf"; origin: DialerOrigin };
  /** Falha na telefonia (registro SIP caiu, originação falhou, perdemos o polling). */
  dialer_connection_error: { phase: "register" | "originate" | "poll_lost"; attempt?: number };

  // ── Leads ───────────────────────────────────────────────────────────────
  /** Tempo que o vendedor passou com a ficha do lead aberta (abrir → fechar). */
  lead_view_duration: { duration_ms: number; lead_status?: string; source?: string };

  // ── Frustração / UX (crucial para o tech leader) ────────────────────────
  /** 3+ cliques no mesmo elemento em <2s: sinal de UI travada ou botão "morto". */
  rage_click: { element: string; clicks: number };
  /** Validação barrou o envio (ex.: número inválido) — layout confuso se repetir. */
  form_validation_error: { form: string; field?: string };
  /** O back recusou o envio do formulário (erro de submit). */
  form_submission_error: { form: string; error_type?: string };
  /** Recarregou a página manualmente (F5) — o que você pediu. */
  page_manual_refresh: { path: string };

  // ── Comportamento / navegação ───────────────────────────────────────────
  /** Quanto tempo a aba ficou escondida (trocou p/ WhatsApp Web, CRM antigo…). */
  visibility_hidden_duration: { duration_ms: number };
  /** Mesmo vendedor com 2+ abas do Lai abertas ao mesmo tempo. */
  app_multi_tab: { tab_count: number };
}

export type AnalyticsEvent = keyof AnalyticsEventMap;

// Assinaturas globais das tags carregadas no index.html.
declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void;
    clarity?: (command: string, ...args: unknown[]) => void;
  }
}

// Dispara um evento custom para o GA4 (e espelha no Clarity, quando fizer sentido,
// para o evento aparecer junto da gravação de sessão). No-op se o gtag não existir.
export function track<K extends AnalyticsEvent>(name: K, params: AnalyticsEventMap[K]): void {
  window.gtag?.("event", name, params);
  // Clarity só aceita um rótulo (sem parâmetros); marca os eventos de fricção para
  // dar para filtrar as gravações por "brigou com a tela".
  if (
    name === "rage_click" ||
    name === "form_validation_error" ||
    name === "dialer_connection_error"
  ) {
    window.clarity?.("event", name);
  }
  // O PostHog recebe TODOS os eventos com os parâmetros inteiros: é lá que se
  // faz funil e retenção, e o filtro acima existe só por limitação do Clarity.
  phCapture(name, params);
}

// page_view manual: numa SPA o gtag NÃO reconta a navegação client-side sozinho.
// O config no index.html tem `send_page_view: false`, então cada troca de rota
// (e a carga inicial) manda o page_view daqui — sem duplicar a primeira visita.
export function trackPageView(path: string, title: string = document.title): void {
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.origin + path,
  });
  phPageView(path, title);
}

// Identidade da sessão. Só o PostHog tem o conceito; GA e Clarity ficam de fora
// de propósito (user_id lá é outra conversa, com implicação de consentimento).
//
// Chamado de UM lugar só (AnalyticsListeners, quando a sessão resolve) — não
// espalhe pelas telas de login, senão um caminho novo de entrada nasce sem
// identificação e ninguém percebe.
export function identifyUser(userId: string): void {
  phIdentify(userId);
}

// Logout. Ver a nota sobre máquina compartilhada em lib/posthog.ts.
export function resetUser(): void {
  phReset();
}
